import { useEffect, useRef, useState } from 'react';

import { useRentalManagerUserStreetEasyWebApiHandler_linkUser } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useAuth } from '../components/AuthProvider';
import { STREET_EASY_LINK_INVALIDATES } from '../constants/InvalidateConstants';

/**
 * Links the session's StreetEasy user to the account so the syndicated listings show up on the first visit. The
 * link is requested once per mount; on success the property lists refetch with the new permissions, and a failure
 * still settles so the page renders whatever the user can already see.
 */
export const useStreetEasyUserLink = () => {
  const { user, isAuthorized, refetch: refetchUser } = useAuth();
  const [isLinkSettled, setIsLinkSettled] = useState(false);
  const hasRequestedLink = useRef(false);
  const isLinkPending = Boolean(user?.streetEasyLinkPending);

  const { mutate: linkStreetEasyUser } = useRentalManagerUserStreetEasyWebApiHandler_linkUser({
    meta: {
      invalidates: [...STREET_EASY_LINK_INVALIDATES],
      context: { api: 'rental-manager', details: 'Linking the StreetEasy user from the properties page' },
    },
  });

  useEffect(() => {
    if (!isAuthorized || !isLinkPending || hasRequestedLink.current) {
      return;
    }

    hasRequestedLink.current = true;
    linkStreetEasyUser(
      { postData: {} },
      {
        onSuccess: () => {
          void refetchUser();
        },
        onSettled: () => {
          setIsLinkSettled(true);
        },
      },
    );
  }, [isAuthorized, isLinkPending, linkStreetEasyUser, refetchUser]);

  return { isLinkingStreetEasyUser: isLinkPending && !isLinkSettled };
};
